import React, { Fragment, useState, useEffect, useCallback } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import AddAppModal from './AddAppModal';
import AppsListMobile from './AppsListMobile';
import Animation from '../../../../../components/Animation';
import { isAppsChanged } from '../../utils';

const emptyArray = [];
const emptyObject = {};

const redirectClarificationMessage = (
  <div className="redirect-clarification-message">
    <h2 className="redirect-clarification-title">Please Select Additional Apps</h2>
    <p className="redirect-clarification-text">
      We need more information before we can generate your report. Please select 2-4 additional
      apps.
    </p>
  </div>
);

const AppsSelectionMobileModal = ({
  preSelectedApp,
  topPopularApps,
  selectionForbidden,
  customTexts = emptyObject,
  onChange,
  onNextStepClick,
  onForbiddenStatusChange,
  coverImage,
  redirectedFlow,
  windowY,
}) => {
  const [selectedApps, setSelectedApps] = useState(preSelectedApp || emptyArray);
  const [showModal, setShowModal] = useState(false);

  const disabledNextStep = selectedApps.length < 3;

  useEffect(() => {
    if (isAppsChanged(preSelectedApp, selectedApps)) {
      onChange(selectedApps);
    }
  }, [selectedApps, showModal]);

  useEffect(() => {
    setSelectedApps((prevState) => {
      if (isAppsChanged(prevState, preSelectedApp)) return preSelectedApp;
      return prevState;
    });
  }, [preSelectedApp]);

  const toggleShowModal = useCallback(() => {
    setShowModal((prevState) => !prevState);
  }, []);

  const handleAppRemove = useCallback((refNo) => {
    setSelectedApps((prevApps) => prevApps.filter(({ ref_no }) => ref_no !== refNo));
  }, []);

  const handleNextClick = () => {
    if (!disabledNextStep) onNextStepClick('userReportForm', false);
  };

  return (
    <Fragment>
      <div className={cx('app-selection-mobile-container-mobile-modal')}>
        {coverImage && (
          <div className="cover-image-box">
            <img src={coverImage} alt="cover mobile" />
          </div>
        )}
        <div className="app-selection-mobile-container-mobile-modal-wrapper">
          {redirectedFlow ? (
            redirectClarificationMessage
          ) : (
            <Fragment>
              <h6 className="app-selection-mobile-title">
                {customTexts.title || 'Insights on Your Competition'}
              </h6>
              {customTexts.subtitle ? (
                <div
                  className="app-selection-mobile-subtitle"
                  dangerouslySetInnerHTML={{ __html: customTexts.subtitle }}
                />
              ) : (
                <p className="app-selection-mobile-subtitle">
                  Select three apps below and learn from <br />
                  your competiton&apos;s mobile footprint
                </p>
              )}
            </Fragment>
          )}

          <AppsListMobile
            apps={selectedApps}
            onAppRemove={handleAppRemove}
            onPlaceholderClick={toggleShowModal}
          />

          <button
            type="button"
            className="btn app-selection-mobile-button"
            disabled={disabledNextStep}
            onClick={handleNextClick}
          >
            {customTexts.button_text || 'Get My Free Report'}
          </button>
        </div>
      </div>

      <Animation show={showModal} animationType="slide-fade-in-out-top">
        <AddAppModal
          selectedApps={selectedApps}
          topPopularApps={topPopularApps}
          selectionForbidden={selectionForbidden}
          onClose={toggleShowModal}
          onChange={setSelectedApps}
          onForbiddenStatusChange={onForbiddenStatusChange}
          windowY={windowY}
        />
      </Animation>
    </Fragment>
  );
};

AppsSelectionMobileModal.propTypes = {
  preSelectedApp: T.array,
  topPopularApps: T.array.isRequired,
  selectionForbidden: T.bool.isRequired,
  customTexts: T.shape({
    title: T.string,
    subtitle: T.string,
    button_text: T.string,
    search_placeholder: T.string,
  }),
  onChange: T.func.isRequired,
  onNextStepClick: T.func.isRequired,
  onForbiddenStatusChange: T.func.isRequired,
  coverImage: T.string,
  redirectedFlow: T.bool,
  windowY: T.number,
};

export default AppsSelectionMobileModal;
